import { BRAND_IDS, DEFAULT_BRAND_ID, type BrandId, type BrandThemeConfig } from "./types";
import { BRAND_TOKEN_KEYS } from "./palette";
import { smartMarketTheme } from "./themes/smartmarket";
import { richPollTheme } from "./themes/richpoll";

// registry.ts — the local brand catalogue.
//
// This is the "local frontend configuration" the loader reads today. When a
// backend config endpoint arrives it replaces the SOURCE behind loadBrandTheme
// (see loader.ts), not this file's shape and not a single consumer.

const REGISTRY: Record<BrandId, BrandThemeConfig> = {
  smartmarket: smartMarketTheme,
  richpoll: richPollTheme,
};

export function isBrandId(value: unknown): value is BrandId {
  return typeof value === "string" && (BRAND_IDS as readonly string[]).includes(value);
}

/**
 * Whatever the environment says → a brand id we can actually render.
 * Unset, misspelled, or a brand we don't ship all land on SmartMarket rather
 * than on a blank screen.
 */
export function resolveBrandId(raw: unknown): BrandId {
  if (isBrandId(raw)) return raw;
  const normalized = typeof raw === "string" ? raw.trim().toLowerCase() : "";
  return isBrandId(normalized) ? normalized : DEFAULT_BRAND_ID;
}

export function getBrandTheme(id: BrandId): BrandThemeConfig {
  return REGISTRY[id];
}

export function listBrandThemes(): BrandThemeConfig[] {
  return BRAND_IDS.map((id) => REGISTRY[id]);
}

/**
 * Structural check on a config from an untrusted source (a future API, a
 * hand-edited local file). A config that fails this is discarded in favour of
 * SmartMarket — a half-applied theme is worse than the default one.
 */
export function isValidBrandTheme(value: unknown): value is BrandThemeConfig {
  if (!value || typeof value !== "object") return false;
  const cfg = value as Partial<BrandThemeConfig>;
  if (!isBrandId(cfg.id)) return false;
  if (!cfg.colors || typeof cfg.colors !== "object") return false;
  const colors = cfg.colors as unknown as Record<string, unknown>;
  for (const key of BRAND_TOKEN_KEYS) {
    const v = colors[key];
    if (typeof v !== "string" || !/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(v.trim())) return false;
  }
  const assets = cfg.assets as Record<string, unknown> | undefined;
  if (!assets) return false;
  for (const key of ["logoWordmark", "logoStacked", "logoMark", "sparkIcon", "currencyIcon"]) {
    if (typeof assets[key] !== "function") return false;
  }
  if (typeof assets.ogImage !== "string" || !assets.ogImage) return false;
  const favicon = assets.favicon as { href?: unknown; type?: unknown } | undefined;
  return !!favicon && typeof favicon.href === "string" && typeof favicon.type === "string";
}

export { BRAND_IDS, DEFAULT_BRAND_ID };
